import React, { useState } from "react";
import { useDispatch } from "react-redux";

import { HamburgerIcon } from "@chakra-ui/icons";
import {
	Alert,
	AlertDescription,
	AlertIcon,
	AlertTitle,
	Badge,
	Box,
	Button,
	Card,
	CardBody,
	CardHeader,
	FormControl,
	FormLabel,
	Heading,
	HStack,
	Icon,
	Input,
	InputGroup,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Select,
	SimpleGrid,
	Td,
	Text,
	Textarea,
	Tr,
	useColorModeValue,
	useDisclosure,
	useToast,
	VStack,
} from "@chakra-ui/react";
import moment from "moment";
import { FiCalendar, FiCheckCircle, FiDollarSign, FiEdit3, FiEye, FiInfo, FiMapPin, FiPackage, FiTrash2, FiUser, FiXCircle } from "react-icons/fi";

import { removeCustomOwnerPlanAsync, updateCustomOwnerPlanAsync } from "../../redux/user/userSlice";
import { getPropertyAddress } from "../../utils/verifier";

export default function CustomOwnerPlansTableRow(props) {
	const toast = useToast();
	const { addedDate, name, description, price, id, properties } = props;
	const initialOwnerPlanInfo = {
		name: name,
		description: description,
		price: price,
		id: id,
		addedDate: addedDate,
	};
	const [editOwnerPlanInfo, setEditOwnerPlanInfo] = useState(initialOwnerPlanInfo);
	const dispatch = useDispatch();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
	const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();

	const [error, setError] = useState({});

	// Color mode values
	const cardBg = useColorModeValue("white", "gray.800");
	const borderColor = useColorModeValue("gray.200", "gray.700");
	const iconColor = useColorModeValue("blue.500", "blue.300");

	const handleChangeOwnerPlanInformation = (e) => {
		const { name, value } = e.target;
		setEditOwnerPlanInfo((prev) => ({ ...prev, [name]: value }));
	};
	const handleSave = () => {
		const errors = {};
		if (Object.keys(errors).length > 0) {
			setError(errors);
			return;
		}
		//save the data
		const payload = { ...editOwnerPlanInfo };
		console.log(payload);
		dispatch(updateCustomOwnerPlanAsync(payload))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Update!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				onClose();
			});
	};
	const handleDelete = () => {
		//delete the data
		console.log("Delete owner plan with id: ", id);
		const payload = { id: id };
		dispatch(removeCustomOwnerPlanAsync(payload))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Delete!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				onDeleteClose();
			});
	};
	const renderEditCustomOwnerPlanModal = () => {
		return (
			<Modal isOpen={isOpen} onClose={onClose} size="2xl">
				<ModalOverlay backdropFilter="blur(10px)" />
				<ModalContent borderRadius="xl" boxShadow="2xl">
					<ModalHeader borderBottom="1px" borderColor={borderColor} pb={4}>
						<HStack spacing={3}>
							<Icon as={FiEdit3} color={iconColor} boxSize={6} />
							<VStack align="start" spacing={0}>
								<Heading size="md">Edit Owner Plan</Heading>
								<Text fontSize="sm" color="gray.500">
									Update plan information
								</Text>
							</VStack>
						</HStack>
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody py={6}>
						<VStack spacing={6}>
							<FormControl>
								<FormLabel fontSize="sm" color="gray.600" mb={2}>
									<HStack spacing={2}>
										<Icon as={FiPackage} boxSize={4} />
										<Text>Name</Text>
									</HStack>
								</FormLabel>
								<Input
									isInvalid={error?.name}
									type="text"
									name="name"
									placeholder="Enter plan name"
									borderRadius="md"
									value={editOwnerPlanInfo.name}
									onChange={handleChangeOwnerPlanInformation}
								/>
							</FormControl>

							<FormControl>
								<FormLabel fontSize="sm" color="gray.600" mb={2}>
									<HStack spacing={2}>
										<Icon as={FiInfo} boxSize={4} />
										<Text>Description</Text>
									</HStack>
								</FormLabel>
								<Textarea
									isInvalid={error?.description}
									name="description"
									placeholder="Enter plan description"
									borderRadius="md"
									value={editOwnerPlanInfo.description}
									onChange={handleChangeOwnerPlanInformation}
									rows={3}
								/>
							</FormControl>

							<FormControl>
								<FormLabel fontSize="sm" color="gray.600" mb={2}>
									<HStack spacing={2}>
										<Icon as={FiDollarSign} boxSize={4} />
										<Text>Price</Text>
									</HStack>
								</FormLabel>
								<InputGroup>
									<Input
										isInvalid={error?.price}
										type="number"
										name="amount"
										placeholder="Enter price"
										borderRadius="md"
										value={editOwnerPlanInfo.price.amount}
										onChange={(e) => {
											setEditOwnerPlanInfo((prev) => ({
												...prev,
												price: { ...prev.price, amount: e.target.value },
											}));
										}}
									/>
									<Select
										name="currency"
										borderRadius="md"
										value={editOwnerPlanInfo.price.currency}
										onChange={(e) => {
											setEditOwnerPlanInfo((prev) => ({
												...prev,
												price: { ...prev.price, currency: e.target.value },
											}));
										}}
									>
										<option value="ron">RON</option>
										<option value="eur">EUR</option>
									</Select>
								</InputGroup>
							</FormControl>
						</VStack>
					</ModalBody>
					<ModalFooter borderTop="1px" borderColor={borderColor} pt={4}>
						<HStack spacing={3}>
							<Button leftIcon={<Icon as={FiXCircle} />} variant="outline" onClick={onClose}>
								Cancel
							</Button>
							<Button colorScheme="blue" leftIcon={<Icon as={FiCheckCircle} />} onClick={handleSave} px={8}>
								Save
							</Button>
						</HStack>
					</ModalFooter>
				</ModalContent>
			</Modal>
		);
	};
	const renderConfirmationModal = () => {
		return (
			<Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
				<ModalOverlay backdropFilter="blur(10px)" />
				<ModalContent borderRadius="xl" boxShadow="2xl">
					<ModalHeader borderBottom="1px" borderColor={borderColor} pb={4}>
						<HStack spacing={3}>
							<Icon as={FiTrash2} color="red.500" boxSize={6} />
							<VStack align="start" spacing={0}>
								<Heading size="md">Confirm Delete</Heading>
								<Text fontSize="sm" color="gray.500">
									This action cannot be undone
								</Text>
							</VStack>
						</HStack>
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody py={6}>
						<Alert status="warning" borderRadius="md" mb={4}>
							<AlertIcon />
							<VStack align="start" spacing={1}>
								<AlertTitle fontSize="sm">Warning</AlertTitle>
								<AlertDescription fontSize="sm">Are you sure you want to delete this owner plan? This action cannot be undone.</AlertDescription>
							</VStack>
						</Alert>
					</ModalBody>
					<ModalFooter borderTop="1px" borderColor={borderColor} pt={4}>
						<HStack spacing={3}>
							<Button leftIcon={<Icon as={FiXCircle} />} variant="outline" onClick={onDeleteClose}>
								Cancel
							</Button>
							<Button colorScheme="red" leftIcon={<Icon as={FiTrash2} />} onClick={handleDelete} px={8}>
								Delete
							</Button>
						</HStack>
					</ModalFooter>
				</ModalContent>
			</Modal>
		);
	};

	const renderViewCustomOwnerPlanModal = () => {
		// check how many properties are using this owner plan and show the property address and owner name
		return (
			<Modal isOpen={isViewOpen} onClose={onViewClose} size="4xl">
				<ModalOverlay backdropFilter="blur(10px)" />
				<ModalContent borderRadius="xl" boxShadow="2xl" maxH="90vh">
					<ModalHeader borderBottom="1px" borderColor={borderColor} pb={4}>
						<HStack spacing={3}>
							<Icon as={FiEye} color={iconColor} boxSize={6} />
							<VStack align="start" spacing={0}>
								<Heading size="md">Service Details</Heading>
								<Text fontSize="sm" color="gray.500">
									View service information and properties
								</Text>
							</VStack>
						</HStack>
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody py={6} overflowY="auto">
						<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
							{/* Left Column - Service Information */}
							<VStack spacing={6} align="stretch">
								{/* Service Summary Card */}
								<Card bg={cardBg} border="1px" borderColor={borderColor} borderRadius="lg">
									<CardHeader pb={3}>
										<HStack spacing={3}>
											<Icon as={FiInfo} color={iconColor} boxSize={5} />
											<Text fontWeight="semibold" fontSize="md">
												Service Summary
											</Text>
										</HStack>
									</CardHeader>
									<CardBody pt={0}>
										<VStack spacing={4} align="stretch">
											<Box>
												<HStack spacing={2} mb={2}>
													<Icon as={FiPackage} color={iconColor} boxSize={4} />
													<Text fontSize="sm" fontWeight="medium">
														Name
													</Text>
												</HStack>
												<Text fontSize="lg" fontWeight="bold">
													{name}
												</Text>
											</Box>

											<Box>
												<HStack spacing={2} mb={2}>
													<Icon as={FiDollarSign} color={iconColor} boxSize={4} />
													<Text fontSize="sm" fontWeight="medium">
														Price
													</Text>
												</HStack>
												<Text fontSize="lg" fontWeight="bold" color="green.500">
													{price.amount} {price.currency}
												</Text>
											</Box>

											<Box>
												<HStack spacing={2} mb={2}>
													<Icon as={FiCalendar} color={iconColor} boxSize={4} />
													<Text fontSize="sm" fontWeight="medium">
														Created
													</Text>
												</HStack>
												<Text fontSize="sm" fontWeight="medium">
													{moment(addedDate).format("DD MMM YYYY - HH:mm")}
												</Text>
											</Box>
										</VStack>
									</CardBody>
								</Card>

								{/* Description Card */}
								<Card bg={cardBg} border="1px" borderColor={borderColor} borderRadius="lg">
									<CardHeader pb={3}>
										<HStack spacing={3}>
											<Icon as={FiInfo} color={iconColor} boxSize={5} />
											<Text fontWeight="semibold" fontSize="md">
												Description
											</Text>
										</HStack>
									</CardHeader>
									<CardBody pt={0}>
										<Text fontSize="sm" color="gray.700" bg="gray.50" p={4} borderRadius="md">
											{description}
										</Text>
									</CardBody>
								</Card>
							</VStack>

							{/* Right Column - Properties */}
							<VStack spacing={6} align="stretch">
								{/* Properties Card */}
								<Card bg={cardBg} border="1px" borderColor={borderColor} borderRadius="lg" flex={1}>
									<CardHeader pb={3}>
										<HStack spacing={3}>
											<Icon as={FiMapPin} color={iconColor} boxSize={5} />
											<Text fontWeight="semibold" fontSize="md">
												Properties Using This Service
											</Text>
											<Badge colorScheme="blue" fontSize="sm">
												{properties.length}
											</Badge>
										</HStack>
									</CardHeader>
									<CardBody pt={0}>
										{properties.length > 0 ? (
											<VStack spacing={3} align="stretch">
												{properties
													.sort((a, b) => a.owner.name > b.owner.name)
													.map((p) => (
														<Box key={p.id} p={3} border="1px" borderColor="gray.200" borderRadius="md" bg="gray.50">
															<HStack spacing={3} mb={2}>
																<Icon as={FiUser} color={iconColor} boxSize={4} />
																<Text fontSize="sm" fontWeight="bold">
																	{p.owner.name}
																</Text>
															</HStack>
															<Text fontSize="sm" color="gray.600" pl={7}>
																{getPropertyAddress(p)}
															</Text>
														</Box>
													))}
											</VStack>
										) : (
											<Box textAlign="center" py={6}>
												<Icon as={FiMapPin} color="gray.300" boxSize={8} mb={3} />
												<Text fontSize="sm" color="gray.500">
													No properties using this service
												</Text>
											</Box>
										)}
									</CardBody>
								</Card>
							</VStack>
						</SimpleGrid>
					</ModalBody>
					<ModalFooter borderTop="1px" borderColor={borderColor} pt={4}>
						<HStack spacing={3}>
							<Button leftIcon={<Icon as={FiXCircle} />} variant="outline" onClick={onViewClose}>
								Close
							</Button>
						</HStack>
					</ModalFooter>
				</ModalContent>
			</Modal>
		);
	};
	return (
		<>
			<Tr>
				<Td>
					<Text fontSize="md" pb=".5rem" whiteSpace={"nowrap"}>
						{name}
					</Text>
				</Td>
				<Td minWidth={{ sm: "250px" }}>
					<Text fontSize="md" pb=".5rem">
						{description}
					</Text>
				</Td>

				<Td>
					<Text fontSize="md" pb=".5rem">
						{price.amount} {price.currency}
					</Text>
				</Td>
				<Td pl="0px">
					<Text fontSize="sm" fontWeight="normal">
						{moment(addedDate).format("DD MMM YYYY - HH:mm")}
					</Text>
				</Td>
				<Td>
					<Menu closeOnSelect={false}>
						<MenuButton>
							<Icon as={HamburgerIcon} />
						</MenuButton>
						<MenuList>
							<MenuItem
								onClick={() => {
									onViewOpen();
								}}
							>
								View
							</MenuItem>
							<MenuItem
								onClick={() => {
									onOpen();
								}}
							>
								Edit
							</MenuItem>
							<MenuItem
								onClick={() => {
									onDeleteOpen();
								}}
							>
								Delete
							</MenuItem>
						</MenuList>
					</Menu>
					{renderEditCustomOwnerPlanModal()}
					{renderConfirmationModal()}
					{renderViewCustomOwnerPlanModal()}
				</Td>
			</Tr>
		</>
	);
}
